import React, { useState } from 'react';
import Button from 'components/_UI/Button';
import Fieldset from 'components/_UI/Fieldset';
import Text from 'components/_UI/Text';
import masks from 'helpers/masks';
import { Column } from 'styled/alignment/Column';
import { StyledForm } from 'styled/UI/StyledForm';
import colors from 'theme/colors';
import { cpf as cpfValidator, cnpj as cnpjValidator, cnpj } from 'cpf-cnpj-validator';
import sizes from 'theme/sizes';
import { useForm } from 'react-hook-form';
import { FullyCentered, HorizontalCenter } from 'styled/alignment/Center';
import fieldValidations from 'helpers/fieldValidations';
import buttonText from 'texts/buttons/buttonText';
import paths from 'routes/paths';
import userApi from 'api/requests/user';
import Loader from 'components/_UI/Loader';
import fieldMessages from 'texts/messages/field';
import styled from 'styled-components';
import unformat from 'helpers/unformat';
import { Row } from 'styled/alignment/Row';
import { Link } from 'react-router-dom';

import ConfirmEmailModal from 'modules/onboarding/sign-in/ConfirmEmailModal';
import { useUser } from 'context/user/User';
import Icon from 'components/_UI/Icon';
import { Check, CheckCircle, User } from 'lucide-react';
import breakpoints from 'theme/breakpoints';
import debounce from 'helpers/debounce';
import PartnerFormStepOne from '../steps/PartnerFormStepOne';
import Progress from 'components/_UI/Progress';
import { Step, StepsContainer } from 'styled/UI/StyledSteps';
import Steps from 'components/_UI/Steps';
import PartnerFormStepTwo from '../steps/PartnerFormStepTwo';
import PartnerFormStepThree from '../steps/PartnerFormStepThree';

export default function PartnerForm() {
    const [currentStep, setCurrentStep] = useState(1);
    const [documentFiles, setDocumentFiles] = useState({ front: null, back: null });

    const steps = {
        one: 1,
        two: 2,
        three: 3
    }
    const lastStep = 3

    const stepValue = (value) => value * (lastStep * 10.5)
    const nextStep = () => setCurrentStep(currentStep + 1)
    const stepBack = () => setCurrentStep(currentStep - 1)

    return (
        <ContainerForm>
            <Column paddingRight='60px' paddingLeft='60px' fullyCentralized>
                <Text paddingBottom={'4px'} type={'bold'}>Passo {currentStep} de {lastStep}</Text>
                <Progress
                    value={stepValue(currentStep)}
                    color={colors.secondaryPurple}
                    bgColor={'#cecece'}
                />
            </Column>

            <Column fullyCentralized paddingRight='60px' paddingLeft='60px'>
                {currentStep === steps.one && <PartnerFormStepOne onNextClick={nextStep} />}
                {currentStep === steps.two && <PartnerFormStepTwo onNextClick={nextStep} onBackClick={stepBack} onFilesChange={setDocumentFiles} documentFiles={documentFiles} />}
                {currentStep === steps.three && <PartnerFormStepThree onNextClick={nextStep} onBackClick={stepBack} documentFiles={documentFiles} />}
            </Column>

        </ContainerForm>
    )

};


const ContainerForm = styled(Column)`
    justify-content: flex-start;
    padding-top: 4.5rem;
    align-items: center;
    overflow-y: auto;

    @media screen and (max-width: ${breakpoints.md}) {
        padding: 30px;
        padding-top: 100px;
        justify-content: center;
        align-items: center;
    }
`;

const LinkTerms = styled(Link)`
    font-size: 12px;
`;

const ContentTiles = styled(Column)`
    align-items: center;
    margin-top: 100px;

    @media screen and (min-width: ${breakpoints.lg}) {
        margin-top: 0;
    }
 
`;

const TextLabel = styled.label`
    margin-left: 10px;
    font-size: 12px;
    color: ${colors.nightDark};
`;

const ContentTypeRole = styled(Column)`
    width: 100%;
    justify-content: center;
    align-items: center;
    position: relative;
    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
    }
    /* @media screen and (min-width: ${breakpoints.lg}) { */
        /* flex-direction: row;
        justify-content: space-between; */
    /* } */
`;

const StyledType = styled(Column)`
    padding: 8px 15px;
    background: ${colors.neutral};
    border: 1px solid ${colors.night};
    border-radius: 10px;
    justify-content: space-around;
    align-items: center;
    width: 100%;
    margin-bottom: 10px;
    height: 170px;
    cursor: pointer;
    transition: all ease-in-out 0.25s;

    /* &:hover {
        transform: scale(1.01)
    } */
    & > p {
        color: ${colors.primaryDashboard};
    }
    ${({ selected }) => selected && `
        background: ${colors.secondaryPurple};
        & > p {
            color: ${colors.neutral} !important;
        }
    `}

    /* @media screen and (min-width: 545px) {
        width: 42%;
    } */
    @media screen and (min-width: ${breakpoints.md}) {    
        &:first-child {
            margin-right: 10px;
        }
    }
    
`;

const ContentStyledIcon = styled.div`
    width: 40px;
    height: 40px;
`;

const RowBoxSelected = styled(Row)`
    width: 100%;
    justify-content: flex-end;
    position: relative; 

    &::before {
        content: '';
        width: 15px;
        height: 15px;
        border: 1px solid ${colors.night};
        border-radius: 50%;
        position: absolute; 
        top: 50%; 
        right: 0; 
        transform: translate(50%, -50%); 
        display: flex; 
        align-items: center; 
        justify-content: center; 
        
        ${({ selected }) => selected && `
            content: '';
            background-color: ${colors.neutral};
            color: ${colors.night}; 
        `}
    }
    
    &::after {
        content: '';
        width: 15px;
        height: 15px;
        border: 1px solid ${colors.night};
        border-radius: 50%;
        position: absolute; 
        top: 50%; 
        right: 0; 
        transform: translate(50%, -50%); 
        display: flex; 
        align-items: center; 
        justify-content: center; 
        
        ${({ selected }) => selected && `
            content: '';
            background-color: ${colors.primaryLight};
            position: absolute;
            right: 0;
            top: 0;
            height: 6px;
            width: 6px;
        `}
    }
`;

const InputDocuments = styled.input`
    width: 100%;
    outline-color: transparent;
    transition: all ease .25s;
    background: transparent;
    outline: transparent;
    color: #707070;
    border: none;
    border-bottom: 2px solid #ACACAC;
    padding: 2px;
    font-weight: 700;
    text-align: center;
    color: #707070;
    margin-bottom: 20px;
`;


// const TextBody = styled.p`
//     font-weight: bold;
//     text-align: center !important;
//     color: ${colors.night} !important;
//     font-size: 12px;
//     max-height: 59px; 
//     font-family: 'Roboto', sans-serif;
// `